import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, ChevronRight, ShieldCheck, AlertCircle, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import VehicleManager from "@/components/VehicleManager";
import { ALL_DOCS } from "@/pages/legal/_content";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import AlertSettings from "@/components/AlertSettings";
import StepUpDialog from "@/components/StepUpDialog";
import IdentityVerify from "@/components/IdentityVerify";
import SelfieCapture from "@/components/SelfieCapture";

const BankSection = () => {
  const { user, refresh } = useAuth();
  const [holder, setHolder] = useState(user?.account_holder_name || "");
  const [account, setAccount] = useState(user?.account_number || "");
  const [ifsc, setIfsc] = useState(user?.ifsc_code || "");
  const [upi, setUpi] = useState(user?.upi_id || "");
  const [busy, setBusy] = useState(false);
  const [stepUp, setStepUp] = useState(false);

  useEffect(() => {
    setHolder(user?.account_holder_name || "");
    setAccount(user?.account_number || "");
    setIfsc(user?.ifsc_code || "");
    setUpi(user?.upi_id || "");
  }, [user?.account_holder_name, user?.account_number, user?.ifsc_code, user?.upi_id]);

  const save = async (step_up_token) => {
    setBusy(true);
    try {
      await api.patch("/users/me", {
        account_holder_name: holder.trim(),
        account_number: account.replace(/\s+/g, ""),
        ifsc_code: ifsc.trim().toUpperCase(),
        upi_id: upi.trim(),
        step_up_token,
      });
      setStepUp(false);
      toast.success("Payout details saved. We'll verify them before your first payout.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(apiError(e, "Couldn't save."));
    } finally { setBusy(false); }
  };

  const verified = !!user?.bank_verified;
  const hasAll = holder && account && ifsc;

  return (
    <div data-testid="profile-bank-section" className="space-y-2">
      {hasAll && (
        <div className={`text-[11px] flex items-center gap-1.5 mb-1 ${verified ? "text-emerald-700" : "text-amber-700"}`}>
          {verified ? <ShieldCheck className="w-3.5 h-3.5"/> : <AlertCircle className="w-3.5 h-3.5"/>}
          {verified ? "Bank verified" : "Awaiting verification by admin"}
        </div>
      )}
      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500">Account holder name <span className="text-rose-500">*</span></label>
        <Input data-testid="bank-holder-input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="As per your PAN"/>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500">Account number <span className="text-rose-500">*</span></label>
        <Input data-testid="bank-account-input" value={account} onChange={(e) => setAccount(e.target.value)} placeholder="6–20 digits"/>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500">IFSC code <span className="text-rose-500">*</span></label>
        <Input data-testid="bank-ifsc-input" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="e.g. HDFC0001234" className="font-mono"/>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-500">UPI ID (optional)</label>
        <Input data-testid="bank-upi-input" value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@bank"/>
      </div>
      <StepUpDialog open={stepUp} purpose="payout" title="Confirm payout details" onClose={() => setStepUp(false)} onVerified={save}/>
      <Button data-testid="bank-save-btn" onClick={() => setStepUp(true)} disabled={busy} className="rounded-full bg-slate-900 hover:bg-slate-800 mt-1">
        {busy ? "Saving…" : "Save bank details"}
      </Button>
    </div>
  );
};

const PhoneChange = () => {
  const [stepUp, setStepUp] = useState(false);
  const [token, setToken] = useState(null);
  const [phone, setPhone] = useState("+91");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const { refresh } = useAuth();
  const request = async () => {
    try { const r = await api.post("/users/me/phone/request", { new_phone: phone, step_up_token: token }); setDevOtp(r.data.dev_otp || "sent"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const confirm = async () => {
    try { await api.post("/users/me/phone/confirm", { new_phone: phone, otp }); toast.success("Phone number updated"); setToken(null); setDevOtp(""); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!token) return (
    <>
      <Button variant="outline" size="sm" className="rounded-full" data-testid="change-phone-btn" onClick={() => setStepUp(true)}>Change phone number</Button>
      <StepUpDialog open={stepUp} purpose="phone" title="Confirm it's you" onClose={() => setStepUp(false)} onVerified={async (t) => { setToken(t); setStepUp(false); }}/>
    </>
  );
  return (
    <div className="space-y-2" data-testid="phone-change-form">
      <Input data-testid="new-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="New phone number"/>
      {!devOtp ? <Button size="sm" className="rounded-full" data-testid="new-phone-send-btn" onClick={request}>Send code to new number</Button> : (
        <div className="flex gap-2">
          <Input data-testid="new-phone-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder={devOtp !== "sent" ? `Dev OTP ${devOtp}` : "6-digit code"}/>
          <Button size="sm" className="rounded-full" data-testid="new-phone-confirm-btn" onClick={confirm}>Confirm</Button>
        </div>
      )}
    </div>
  );
};

const ProfileDialog = ({ triggerClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const { user, mode, logout } = useAuth();
  const nav = useNavigate();
  const carrier = mode === "carrier";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="open-profile-btn" className={triggerClassName}>
          <User className="w-4 h-4"/>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>My profile</DialogTitle>
          <DialogDescription>
            Your account, verification{carrier ? ", vehicles and payout details" : ""}, and CarryIt's legal documents.
          </DialogDescription>
        </DialogHeader>

        <section className="pt-2" data-testid="profile-account-section">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Account</h3>
          <div className="text-sm"><span className="font-semibold" data-testid="profile-display-name">{user?.name}</span> <span className="text-slate-500">· {user?.phone}</span></div>
          <div className="mt-3"><PhoneChange/></div>
        </section>

        <section className="pt-6 border-t border-slate-100 mt-4">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Identity verification</h3>
          <IdentityVerify/>
          <p className="text-[11px] text-slate-500 mt-2">Verified once for your whole account — both Send and Carry.</p>
        </section>

        {carrier && (
          <>
            <section className="pt-6 border-t border-slate-100 mt-4">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Profile photo</h3>
              <SelfieCapture/>
            </section>
            <section className="pt-6 border-t border-slate-100 mt-4">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Saved vehicles</h3>
              <VehicleManager />
            </section>
            <section className="pt-6 border-t border-slate-100 mt-4">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Payout details</h3>
              <p className="text-[11px] text-slate-500 mb-3">Not needed to post trips. Add a bank account or UPI ID before you withdraw — it's verified once before your first payout. Changing it later needs a fresh OTP.</p>
              <BankSection />
            </section>
            <section className="pt-6 border-t border-slate-100 mt-4">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Parcel alerts & location</h3>
              <AlertSettings />
            </section>
          </>
        )}

        <section className="pt-6 border-t border-slate-100 mt-4">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Legal & policies</h3>
          <div className="space-y-1.5" data-testid="profile-legal-links">
            {ALL_DOCS.map((d) => (
              <Link
                key={d.slug}
                to={`/${d.slug}`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                data-testid={`menu-link-${d.slug}`}
              >
                <span>{d.title}</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400"/>
              </Link>
            ))}
          </div>
        </section>

        <section className="pt-6 border-t border-slate-100 mt-4">
          <Button variant="outline" data-testid="logout-btn" className="w-full rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                  onClick={async () => { setOpen(false); await logout(); nav("/"); }}>
            <LogOut className="w-4 h-4 mr-1.5"/> Log out
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;
